import type { ReactNode, SVGProps } from "react";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "stroke" | "fill"> {
  size?: number;
  stroke?: string;
  fill?: string;
  sw?: number;
}

interface IcoBaseProps extends IconProps {
  path?: string;
  children?: ReactNode;
}

function Ico({
  size = 16,
  stroke = "currentColor",
  fill = "none",
  sw = 1.6,
  path,
  children,
  ...rest
}: IcoBaseProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {path ? <path d={path} /> : children}
    </svg>
  );
}

export const IcoBoard = (p: IconProps) => (
  <Ico {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M8 4v16M16 4v16" />
  </Ico>
);

export const IcoSearch = (p: IconProps) => (
  <Ico {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </Ico>
);

export const IcoPlus = (p: IconProps) => (
  <Ico {...p}>
    <path d="M12 5v14M5 12h14" />
  </Ico>
);

export const IcoLogout = (p: IconProps) => (
  <Ico {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </Ico>
);

export const IcoClose = (p: IconProps) => (
  <Ico {...p}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Ico>
);

export const IcoMenu = (p: IconProps) => (
  <Ico {...p}>
    <path d="M3 6h18M3 12h18M3 18h18" />
  </Ico>
);

export const IcoCopy = (p: IconProps) => (
  <Ico {...p}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Ico>
);

export const IcoTrash = (p: IconProps) => (
  <Ico {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
  </Ico>
);

export const IcoChevDown = (p: IconProps) => (
  <Ico {...p}>
    <path d="M6 9l6 6 6-6" />
  </Ico>
);

export const IcoCheck = (p: IconProps) => (
  <Ico {...p}>
    <path d="M20 6L9 17l-5-5" />
  </Ico>
);

export const IcoSparkle = (p: IconProps) => (
  <Ico {...p}>
    <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5zM19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </Ico>
);

export const IcoArchive = (p: IconProps) => (
  <Ico {...p}>
    <rect x="2" y="3" width="20" height="5" rx="1" />
    <path d="M4 8v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8M10 12h4" />
  </Ico>
);

export const IcoBroadcast = (p: IconProps) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="2" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49M20.07 4.93a10 10 0 0 1 0 14.14M3.93 19.07a10 10 0 0 1 0-14.14" />
  </Ico>
);

export const IcoMic = (p: IconProps) => (
  <Ico {...p}>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M19 10a7 7 0 0 1-14 0M12 17v4M8 21h8" />
  </Ico>
);

export const IcoStack = (p: IconProps) => (
  <Ico {...p}>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <rect x="3" y="11" width="18" height="4" rx="1" />
    <rect x="3" y="18" width="18" height="3" rx="1" />
  </Ico>
);

export const IcoBolt = (p: IconProps) => (
  <Ico {...p}>
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </Ico>
);

export const IcoBell = (p: IconProps) => (
  <Ico {...p}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </Ico>
);

export const IcoTag = (p: IconProps) => (
  <Ico {...p}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <circle cx="7" cy="7" r="1.2" fill="currentColor" />
  </Ico>
);

export const IcoDoc = (p: IconProps) => (
  <Ico {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </Ico>
);

export const IcoTemplate = (p: IconProps) => (
  <Ico {...p}>
    <rect x="3" y="3" width="18" height="7" rx="2" />
    <rect x="3" y="14" width="11" height="7" rx="2" />
    <rect x="17" y="14" width="4" height="7" rx="2" />
  </Ico>
);

export const IcoChevLeft = (p: IconProps) => (
  <Ico {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Ico>
);

export const IcoEdit = (p: IconProps) => (
  <Ico {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </Ico>
);

export const IcoClock = (p: IconProps) => (
  <Ico {...p}>
    <circle cx="11" cy="11" r="9" />
    <path d="M11 6v5l3 2" />
  </Ico>
);

export const IcoMore = (p: IconProps) => (
  <Ico {...p} sw={p.sw ?? 2}>
    <circle cx="5" cy="12" r="1.3" fill="currentColor" />
    <circle cx="12" cy="12" r="1.3" fill="currentColor" />
    <circle cx="19" cy="12" r="1.3" fill="currentColor" />
  </Ico>
);

export const IcoUsers = (p: IconProps) => (
  <Ico {...p}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Ico>
);

export const IcoDragGrip = (p: IconProps) => (
  <Ico {...p} fill="currentColor" stroke="none">
    <circle cx="9" cy="6" r="1.4" />
    <circle cx="15" cy="6" r="1.4" />
    <circle cx="9" cy="12" r="1.4" />
    <circle cx="15" cy="12" r="1.4" />
    <circle cx="9" cy="18" r="1.4" />
    <circle cx="15" cy="18" r="1.4" />
  </Ico>
);
